// FILE: src/integrations/supabase/client.ts
// ROLE: Creates the global Supabase client used by the app (web + Capacitor iOS)
// PURPOSE: Enable PKCE + ensure PKCE code_verifier persists on native via Capacitor Preferences AND on web via localStorage adapter
// SECTION: Imports + Env helpers + PKCE native storage + createClient config
// MARKER: TASK34_PKCE_CAP_STORAGE_FULLFILE_2026_02_07_B

import { createClient } from "@supabase/supabase-js";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";

function mustGetEnv(key: string): string {
  const v = (import.meta as any).env?.[key];
  if (typeof v === "string" && v.length > 0) return v;

  throw new Error(`[supabase] Missing ${key}. Add it to .env (local) or Vercel env (prod).`);
}

/**
 * Support BOTH env names to avoid breaking older deployments:
 * - VITE_SUPABASE_ANON_KEY (preferred)
 * - VITE_SUPABASE_PUBLISHABLE_KEY (legacy)
 */
function getAnonKey(): string {
  const env = (import.meta as any).env || {};
  const a = env.VITE_SUPABASE_ANON_KEY;
  const b = env.VITE_SUPABASE_PUBLISHABLE_KEY;

  const key =
    (typeof a === "string" && a.length > 0 && a) ||
    (typeof b === "string" && b.length > 0 && b) ||
    "";

  if (!key) {
    throw new Error(
      `[supabase] Missing VITE_SUPABASE_ANON_KEY (or legacy VITE_SUPABASE_PUBLISHABLE_KEY). Add it to .env / Vercel env.`
    );
  }
  return key;
}

export const SUPABASE_URL = mustGetEnv("VITE_SUPABASE_URL");
console.log("[SUPABASE_URL_RUNTIME]", SUPABASE_URL);

export const SUPABASE_ANON_KEY = getAnonKey();

// SECTION: Capacitor Preferences storage adapter for Supabase Auth (PKCE verifier/state persistence)
// MARKER: TASK34_LOG_STORAGE_KEYS_2026_02_08_A
const PKCE_STORAGE_MARKER_TASK1_CACHE_2026_02_11_A = "PKCE_STORAGE_MARKER_TASK1_CACHE_2026_02_11_A";

// Only log storage ops when explicitly enabled.
// Enable locally with: localStorage.setItem("SPMAI_DEBUG_PKCE_STORAGE","1")
function shouldLogPkceStorage(): boolean {
  try {
    if (typeof window === "undefined") return false;
    return window.localStorage?.getItem("SPMAI_DEBUG_PKCE_STORAGE") === "1";
  } catch {
    return false;
  }
}

// In-memory cache to prevent repeated native Preferences.get() calls.
const pkceMemCache = new Map<string, string | null>();
const pkceInFlight = new Map<string, Promise<string | null>>();

function unwrapIfJsonStringLiteral(value: string): string {
  if (value.length >= 2 && value.startsWith('"') && value.endsWith('"')) {
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === "string") return parsed;
    } catch {
      // ignore
    }
  }
  return value;
}

// SECTION (S1): Native Capacitor storage adapter
// MARKER: SUPABASE_NATIVE_PKCE_STORAGE_2026_02_13_A
const capacitorStorage = {
  getItem: async (key: string) => {
    if (pkceMemCache.has(key)) {
      const cached = pkceMemCache.get(key) ?? null;
      if (shouldLogPkceStorage()) {
        console.log("[pkce.storage.getItem][mem]", { key, has: cached != null, len: cached?.length ?? 0 });
      }
      return cached;
    }

    const existing = pkceInFlight.get(key);
    if (existing) return existing;

    const p = (async () => {
      const { value } = await Preferences.get({ key });

      if (shouldLogPkceStorage()) {
        console.log("[pkce.storage.getItem][native]", { key, has: value != null, len: value?.length ?? 0 });
      }

      if (value == null) {
        pkceMemCache.set(key, null);
        return null;
      }

      const unwrapped = unwrapIfJsonStringLiteral(value);
      pkceMemCache.set(key, unwrapped);
      return unwrapped;
    })().finally(() => {
      pkceInFlight.delete(key);
    });

    pkceInFlight.set(key, p);
    return p;
  },

  setItem: async (key: string, value: string) => {
    const toStore = unwrapIfJsonStringLiteral(value);
    pkceMemCache.set(key, toStore);

    if (shouldLogPkceStorage()) {
      console.log("[pkce.storage.setItem][native]", { key, len: toStore?.length ?? 0 });
    }

    await Preferences.set({ key, value: toStore });
  },

  removeItem: async (key: string) => {
    pkceMemCache.set(key, null);

    if (shouldLogPkceStorage()) {
      console.log("[pkce.storage.removeItem][native]", { key });
    }

    await Preferences.remove({ key });
  },
};

// SECTION: WEB storage adapter for Supabase Auth (loggable wrapper)
// PURPOSE: Prove whether PKCE code_verifier is being written BEFORE redirect
// MARKER: SUPABASE_WEB_STORAGE_WRAPPER_2026_02_13_A
function shouldLogWebAuthStorage(): boolean {
  try {
    if (typeof window === "undefined") return false;
    return window.localStorage?.getItem("SPMAI_DEBUG_WEB_AUTH_STORAGE") === "1";
  } catch {
    return false;
  }
}

// SECTION (WEB-101): Web auth storage (safe + write-through + loggable)
// ROLE: Persist PKCE + session keys reliably on web
// PURPOSE: Ensure code_verifier/state are actually written before OAuth redirect
// MARKER: SUPABASE_WEB_AUTH_STORAGE_SAFE_2026_02_13_A
const webAuthMemFallback = new Map<string, string>();

function safeLsGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeLsSet(key: string, value: string): boolean {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}
function safeLsRemove(key: string): boolean {
  try {
    window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

function safeSsGet(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSsSet(key: string, value: string): boolean {
  try {
    window.sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}
function safeSsRemove(key: string): boolean {
  try {
    window.sessionStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

const webAuthStorage = {
  getItem: (key: string) => {
    // Try localStorage, then sessionStorage, then memory fallback
    const v = safeLsGet(key) ?? safeSsGet(key) ?? webAuthMemFallback.get(key) ?? null;

    if (shouldLogWebAuthStorage()) {
      console.log("[web.auth.storage.getItem]", {
        key,
        has: v != null,
        len: v?.length ?? 0,
        from:
          safeLsGet(key) != null
            ? "localStorage"
            : safeSsGet(key) != null
            ? "sessionStorage"
            : webAuthMemFallback.has(key)
            ? "mem"
            : "none",
      });
    }
    return v;
  },

  setItem: (key: string, value: string) => {
    // Always log intent first (so we can see it even if storage throws)
    if (shouldLogWebAuthStorage()) {
      console.log("[web.auth.storage.setItem]", { key, len: value?.length ?? 0 });
    }

    // Write-through: localStorage + sessionStorage + memory fallback
    const okLs = safeLsSet(key, value);
    const okSs = safeSsSet(key, value);
    webAuthMemFallback.set(key, value);

    if (shouldLogWebAuthStorage()) {
      console.log("[web.auth.storage.setItem.result]", {
        key,
        okLocalStorage: okLs,
        okSessionStorage: okSs,
        mem: true,
      });
    }
  },

  removeItem: (key: string) => {
    if (shouldLogWebAuthStorage()) {
      console.log("[web.auth.storage.removeItem]", { key });
    }

    const okLs = safeLsRemove(key);
    const okSs = safeSsRemove(key);
    webAuthMemFallback.delete(key);

    if (shouldLogWebAuthStorage()) {
      console.log("[web.auth.storage.removeItem.result]", {
        key,
        okLocalStorage: okLs,
        okSessionStorage: okSs,
        mem: true,
      });
    }
  },
};

// SECTION (OAUTH-201): Web-safe OAuth (skip redirect until PKCE verifier is written)
// ROLE: Prevent PKCE timing race by controlling redirect ourselves
// PURPOSE: Confirm code_verifier exists in storage before navigating away
// MARKER: SUPABASE_WEB_OAUTH_SAFE_REDIRECT_2026_02_13_A

function listStorageKeysSafe(kind: "local" | "session"): string[] {
  try {
    const s = kind === "local" ? window.localStorage : window.sessionStorage;
    const keys: string[] = [];
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i);
      if (k) keys.push(k);
    }
    return keys;
  } catch {
    return [];
  }
}

function findNewKeys(before: string[], after: string[]): string[] {
  const b = new Set(before);
  return after.filter((k) => !b.has(k));
}

function containsPkceVerifierKey(keys: string[]): boolean {
  // handle both dash + underscore variants, and allow prefixes (sb-...-auth-token-*)
  return keys.some((k) => k.toLowerCase().includes("code-verifier") || k.toLowerCase().includes("code_verifier"));
}

async function waitForPkceVerifierWrite(beforeLocal: string[], beforeSession: string[]): Promise<boolean> {
  const start = Date.now();
  const timeoutMs = 2000;
  const intervalMs = 25;

  while (Date.now() - start < timeoutMs) {
    const afterLocal = listStorageKeysSafe("local");
    const afterSession = listStorageKeysSafe("session");

    const newLocal = findNewKeys(beforeLocal, afterLocal);
    const newSession = findNewKeys(beforeSession, afterSession);

    const hit =
      containsPkceVerifierKey(afterLocal) ||
      containsPkceVerifierKey(afterSession) ||
      containsPkceVerifierKey(newLocal) ||
      containsPkceVerifierKey(newSession);

    if (hit) return true;
    await new Promise((r) => setTimeout(r, intervalMs));
  }

  return false;
}


// SECTION (S3): Supabase client
// MARKER: TASK34_SUPABASE_CLIENT_CREATE_2026_02_13_A
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    // SECTION: PKCE flow for OAuth
    // MARKER: TASK34_SUPABASE_PKCE_FLOWTYPE_2026_02_07_A
    flowType: "pkce",
    persistSession: true,
    autoRefreshToken: true,

    // Web parses redirect URL; native uses deep link handler
    detectSessionInUrl: !Capacitor.isNativePlatform(),

    // SECTION: Storage selection by platform
    // MARKER: TASK34_SUPABASE_STORAGE_SPLIT_WEB_NATIVE_2026_02_13_A
    storage: Capacitor.isNativePlatform() ? capacitorStorage : webAuthStorage,
  },
  global: {
    headers: {
      "X-Client-Info": "salespitchmasterai-web",
    },
  },
});
// SECTION (WEBSAFE-OAUTH): Web-only OAuth helper to avoid PKCE timing races
// ROLE: Initiate OAuth without immediate redirect, then redirect manually after verifier write
// PURPOSE: Prevent "PKCE code verifier not found in storage" on web
// MARKER: SIGNIN_WITH_OAUTH_WEBSAFE_EXPORT_2026_02_13_A
export async function signInWithOAuthWebSafe(params: {
  provider: "google";
  redirectTo: string;
  scopes?: string;
}): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    throw new Error("[auth] signInWithOAuthWebSafe called on native platform.");
  }

  const { provider, redirectTo, scopes } = params;

  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo,
      scopes,
      // Critical: do NOT redirect automatically; we redirect after we confirm verifier exists
      skipBrowserRedirect: true,
    },
  });

  if (error) throw new Error(`[auth] signInWithOAuth failed: ${error.message}`);
  const url = (data as any)?.url as string | undefined;
  if (!url) throw new Error("[auth] signInWithOAuth did not return a redirect URL.");

  // Minimal wait loop to allow storage write to complete before leaving page.
  // NOTE: Supabase uses keys like: sb-<project-ref>-auth-token-code-verifier
  const startedAt = Date.now();
  const timeoutMs = 1500;
  const verifierKeyHint = "auth-token-code-verifier";

  let verifierFound = false;

  while (Date.now() - startedAt < timeoutMs) {
    try {
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i);
        if (k && k.includes(verifierKeyHint)) {
          const v = window.localStorage.getItem(k);
          if (v && v.length > 20) {
            verifierFound = true;
            break;
          }
        }
      }
    } catch {}

    if (verifierFound) break;
    await new Promise((r) => setTimeout(r, 25));
  }

  if (!verifierFound) {
    throw new Error("[auth] PKCE verifier was not written before redirect. Blocking redirect.");
  }

  window.location.assign(url);
}

export async function invokeFunction<T = any>(name: string, body: Record<string, any>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    throw new Error(`[functions.invoke:${name}] ${error.message || "Unknown error"}`);
  }
  return data as T;
}
